import katex from "katex";
import { useMemo, useState } from "react";
import type { MathpixResult } from "#/lib/mathpix";

export function ReviewStage({
	questionImageUrl,
	workingOutImageUrl,
	mathpix,
	onComplete,
	onBack,
	onRetryRecognize,
}: {
	questionImageUrl: string;
	workingOutImageUrl: string;
	mathpix: MathpixResult;
	onComplete: (finalLatex: string) => void | Promise<void>;
	onBack: () => void;
	onRetryRecognize: () => void | Promise<void>;
}) {
	const initialLatex = mathpix.latexStyled ?? mathpix.text ?? "";
	const [latex, setLatex] = useState(initialLatex);

	const rendered = useMemo(() => {
		try {
			return katex.renderToString(latex || "\\text{(empty)}", {
				throwOnError: false,
				displayMode: true,
			});
		} catch (e) {
			return `<span style="color:#dc2626">${(e as Error).message}</span>`;
		}
	}, [latex]);

	return (
		<div className="absolute inset-0 overflow-auto p-4">
			<div className="mx-auto max-w-5xl grid gap-4 lg:grid-cols-2">
				<div>
					<h2 className="text-sm font-semibold text-zinc-500 uppercase tracking-wide mb-2">
						Question
					</h2>
					<img
						src={questionImageUrl}
						alt="Math question"
						className="rounded border max-h-72 object-contain bg-white"
					/>
					<h2 className="text-sm font-semibold text-zinc-500 uppercase tracking-wide mt-4 mb-2">
						Your working out
					</h2>
					<img
						src={workingOutImageUrl}
						alt="Working out"
						className="rounded border max-h-72 object-contain bg-white"
					/>
				</div>
				<div className="flex flex-col gap-4">
					<div>
						<div className="flex items-center justify-between mb-2">
							<h2 className="text-sm font-semibold text-zinc-500 uppercase tracking-wide">
								Mathpix output
							</h2>
							<div className="flex items-center gap-2">
								{mathpix.confidence !== null && (
									<span className="text-xs text-zinc-500">
										conf {(mathpix.confidence * 100).toFixed(0)}%
									</span>
								)}
								<button
									type="button"
									onClick={onRetryRecognize}
									className="text-xs text-blue-600 hover:underline"
								>
									Re-recognize
								</button>
							</div>
						</div>
						<div
							className="rounded border bg-white px-3 py-4 min-h-20 text-lg overflow-auto"
							// biome-ignore lint/security/noDangerouslySetInnerHtml: KaTeX output is trusted, sanitized HTML
							dangerouslySetInnerHTML={{ __html: rendered }}
						/>
					</div>
					<div>
						<label
							htmlFor="latex-edit"
							className="block text-sm font-semibold text-zinc-500 uppercase tracking-wide mb-2"
						>
							LaTeX (editable)
						</label>
						<textarea
							id="latex-edit"
							value={latex}
							onChange={(e) => setLatex(e.target.value)}
							rows={6}
							className="w-full rounded border p-3 font-mono text-sm"
							spellCheck={false}
						/>
					</div>
					<div className="flex gap-3 mt-2">
						<button
							type="button"
							onClick={onBack}
							className="px-4 py-2 rounded-lg border text-sm hover:bg-zinc-50"
						>
							Back to canvas
						</button>
						<button
							type="button"
							onClick={() => onComplete(latex)}
							className="ml-auto px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium shadow hover:bg-emerald-700"
						>
							Complete question
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
